import {
  Component,
  computed,
  effect,
  EffectRef,
  input,
  InputSignal,
  model,
  ModelSignal,
  OnDestroy,
  output,
  OutputEmitterRef,
  Signal,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { QuantityCounterInputModel } from '@libs/form/models';
import { debounceTime, Subscription } from 'rxjs';
import { QuantityCounterComponent } from './quantity-counter.component';

@Component({
  selector: 'ui-quantity-counter',
  template: '<ui-form-quantity-counter (remove)="onRemove()" [formControl]="quantityCounterControl" [data]="data()" [loading]="loading()"></ui-form-quantity-counter>',
  styleUrls: ['./quantity-counter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    QuantityCounterComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class QuantityCounterAdapterComponent implements OnDestroy {
  private subscription: Subscription = new Subscription();

  private readonly valueEffectRef: EffectRef = effect(() => {
    this.quantityCounterControl.setValue(this.value());
  });

  private readonly disabledStateEffectRef: EffectRef = effect(() => {
    if (this.disabled()) {
      this.quantityCounterControl.disable();
    } else {
      this.quantityCounterControl.enable();
    }
  });

  private readonly valueChangeListenerEffectRef: EffectRef = effect(() => {
    this.initValueChangeListener();
  });

  public value: ModelSignal<number> = model.required();
  public name: InputSignal<string> = input('quantity');
  public label: InputSignal<string | undefined> = input();
  public labelInfo: InputSignal<string | undefined> = input();
  public placeholder: InputSignal<string | undefined> = input();
  public hint: InputSignal<string | undefined> = input();
  public disabled: InputSignal<boolean> = input(false);
  public showRemoveWhenQuantityIsMinimum: InputSignal<boolean> = input<boolean>(false);
  public loading: InputSignal<boolean> = input<boolean>(false);
  public debounceMs: InputSignal<number> = input(800);
  public max: InputSignal<number | undefined> = input();
  public min: InputSignal<number | undefined> = input();

  public remove: OutputEmitterRef<void> = output();

  public data: Signal<QuantityCounterInputModel> = computed(() => {
    return {
      type: 'quantityCounter',
      name: this.name(),
      label: this.label(),
      labelInfo: this.labelInfo(),
      placeholder: this.placeholder(),
      hint: this.hint(),
      value: this.value(),
      showRemoveWhenQuantityIsMinimum: this.showRemoveWhenQuantityIsMinimum(),
      attributes: {
        disabled: this.disabled(),
        max: this.max()?.toString(),
        min: this.min()?.toString(),
      },
    };
  });

  public quantityCounterControl: UntypedFormControl = new UntypedFormControl();

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public onRemove(): void {
    this.initValueChangeListener();
    this.remove.emit();
  }

  private initValueChangeListener(): void {
    this.subscription.unsubscribe();
    this.subscription = new Subscription();

    this.subscription.add(this.quantityCounterControl.valueChanges.pipe(debounceTime(this.debounceMs())).subscribe((value: number) => {
      if (this.value() === value) {
        return;
      }

      untracked(() => {
        this.value.set(value);
      });
    }));
  }
}
